<template>
  <div class="image-upload-container">

    <h2 class="image-upload-h2" >
      {{strings.image_upload_title}}
    </h2>
    <p class="image-upload-p">
      {{strings.image_upload_description}}
    </p>
    <div class="image-upload-list" v-html="imageUploadDiscList" />

    <div v-if="showImageUploader" class="container form-container">
      <div
        class="alert"
        :class="{
          success: showSuccessMessage,
          error: showErrorMessage || showMustAgree || showSizeLimitMessage,
          info:
            showInfoMessage ||
            showNotLoggedInMessage ||
            showAlreadyUploadedMessage,
        }"
        v-if="showAlert"
      >
        <div class="alert-content">
          <p v-if="showSuccessMessage">{{strings.alert_image_upload_successfully}}</p>
          <p v-if="showErrorMessage">
            {{strings.alert_error_message_uploading}} {{ errorMessage }}
          </p>
          <p v-if="showInfoMessage">{{strings.alert_please_select_a_file_first}}</p>
          <p v-if="showSizeLimitMessage">{{strings.error_message_file_size}}</p>
          <p v-if="showNotLoggedInMessage">
            {{strings.alert_not_logged_in_message}}
          </p>
          <p v-if="showAlreadyUploadedMessage">
            {{strings.alert_already_uploaded_message}}
          </p>
          <p v-if="showMustAgree">
            {{strings.alert_message_agree_to_be_contacted}}
          </p>
          <NsIcon
            v-if="showSuccessMessage"
            icon-name="icon-success"
            size="icon-md"
            color="#5BAC85"
            class="alert-icon"
          />
          <NsIcon
            v-if="showErrorMessage || showMustAgree || showSizeLimitMessage"
            icon-name="icon-warning"
            size="icon-md"
            class="alert-icon"
            color="#D8000C"
          />
          <NsIcon
            v-if="
              showInfoMessage ||
              showNotLoggedInMessage ||
              showAlreadyUploadedMessage
            "
            icon-name="icon-info"
            size="icon-md"
            color="#3783B9"
            class="alert-icon"
          />
        </div>
        <div class="alert-action-wrapper">
          <button
            @click="handleCloseAlert"
            class="close-button"
            :class="{
              success: showSuccessMessage,
              error: showErrorMessage || showMustAgree || showSizeLimitMessage,
              info:
                showInfoMessage ||
                showNotLoggedInMessage ||
                showAlreadyUploadedMessage,
            }"
          >
            {{strings.alert_close_button}}
          </button>
        </div>
      </div>
      <div class="file-upload-wrapper">
        <div class="upper-section">
          <input type="file" @change="handleFileUpload" />
        </div>
        <div class="lower-section">
          <div class="checkbox-container">
            <input
              type="checkbox"
              id="agree"
              class="md-checkbox"
              v-model="agree"
            />
            <label
for="agree"
              >{{strings.form_agree_label}}</label
            >
          </div>
          <button
            @click="submitImage"
            class="primary submit-button"
            :class="{ loading: loading }"
            :disabled="showSizeLimitMessage"
          >
            <span v-if="loading">{{strings.form_loading}}</span>
            <span v-if="!loading">{{strings.form_upload_image}}</span>
          </button>
        </div>
      </div>
    </div>
    <div v-else class="container login-wrapper">
      <p><em>{{strings.form_login_text}}</em></p>
      <button class="primary" @click="handleLogin">{{strings.login_button_text}}</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config';
import {NsIcon} from '@nuskin/ns-icon';

export default {
    name: 'ImageUploader',
    components: {
        NsIcon
    },
    props: {
        imageUploadDiscList: String,
        config: {
            type: Object,
            required: true
        },
        strings: {
            type: Object
        }
    },
    data() {
        return {
            selectedFile: null,
            showImageUploader: false,
            account: null,
            tokens: null,
            userInfo: null,
            env: config.env,
            loading: false,
            showSuccessMessage: false,
            showErrorMessage: false,
            showInfoMessage: false,
            showAlreadyUploadedMessage: false,
            showNotLoggedInMessage: false,
            showMustAgree: false,
            showAlert: false,
            showSizeLimitMessage: false,
            errorMessage: '',
            agree: false,
            maxFileSize: 2621440 // 2.5 MB in bytes
        };
    },
    mounted() {
        this.checkTokens();
    },
    computed: {
        loginUrl() {
            return `${
                this.env === 'production' ?
                    'https://www.nuskin.com' :
                    'https://test.nuskin.com'
            }/content/login/corporate/${this.currentLanguage !== 'ko' ? this.currentLanguage : 'en'}.html?destination=${
                this.env === 'production' ?
                    'https://www.nuskin.com' :
                    'https://test.nuskin.com'
            }/static/india-opening?locale=${this.currentLanguage}`;
        },
        currentLanguage() {
            const lang = this.$route.query.lang?.split('-')[1];
            return lang || 'en';
        }
    },

    methods: {
        handleFileUpload(event) {
            const file = event.target.files[0];
            if (file && file.size > this.maxFileSize) {
                this.selectedFile = null;
                this.showSizeLimitMessage = true;
                this.showAlert = true;
            } else {
                this.selectedFile = file;
            }
        },

        checkTokens() {
            this.tokens = sessionStorage.getItem('oktaTokens');
            this.userInfo = localStorage.getItem('nuskin.account.data');

            if (this.tokens && this.userInfo) {
                this.showImageUploader = true;
                this.account = JSON.parse(
                    localStorage.getItem('nuskin.account.data')
                ).user;

                const accountInfo = {
                    accountId: this.account.id,
                    familyName: this.account.lastName || this.account.localName,
                    givenName: this.account.firstName,
                    email: this.account.email,
                    personId: this.account.personId,
                    country: this.account.countryCd
                };
                this.account = accountInfo;
            } else {
                this.showImageUploader = false;
                this.loading = false;
            }
        },

        async submitImage() {
            this.loading = true;
            if (!this.selectedFile) {
                this.showInfoMessage = true;
                this.showAlert = true;
                this.loading = false;
                return;
            }
            if (!this.agree) {
                this.showMustAgree = true;
                this.showAlert = true;
                this.loading = false;
                return;
            }
            const stringifyAccount = JSON.stringify(this.account);
            const formData = new FormData();
            formData.append('accountData', stringifyAccount);
            formData.append('image', this.selectedFile);
            const token = JSON.parse(this.tokens).accessToken;
            const options = {
                method: 'POST',
                url: `${process.env.VUE_APP_SERVICE_BASE}/india-splash-form/v1/splashes`,
                headers: {
                    'Content-Type':
            'multipart/form-data; boundary=---011000010111000001101001',
                    'Authorization': `Bearer ${token}`
                },
                data: formData
            };
            axios(options)
                .then((response) => {
                    this.loading = false;
                    this.showSuccessMessage = true;
                    this.showAlert = true;
                    this.showErrorMessage = false;
                    this.showInfoMessage = false;
                    this.showMustAgree = false;
                })
                .catch((error) => {
                    console.error({error}, 'error');
                    this.loading = false;
                    this.showSuccessMessage = false;
                    this.showErrorMessage = true;
                    this.showAlert = true;
                    this.errorMessage = error.message;
                    this.showInfoMessage = false;
                    if (error.response && error.response.status === 409) {
                        // Handle 409 error here
                        this.showAlreadyUploadedMessage = true;
                        this.showErrorMessage = false;
                    } else if (error.response && error.response.status === 401) {
                        this.showNotLoggedInMessage = true;
                    } else {
                        this.showNotLoggedInMessage = false;
                        this.showAlreadyUploadedMessage = false;
                    }
                });
        },
        handleLogin() {
            window.location = this.loginUrl;
        },
        handleCloseAlert() {
            this.showSuccessMessage = false;
            this.showErrorMessage = false;
            this.showInfoMessage = false;
            this.showAlreadyUploadedMessage = false;
            this.showNotLoggedInMessage = false;
            this.showAlert = false;
            this.showMustAgree = false;
            this.showSizeLimitMessage = false;
        }
    }
};
</script>
<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/buttons";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/forms";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

.image-upload-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  max-width: 1440px;
  margin: 20px auto 0;
  padding: 0 20px;
  h2 {
    font-family: Lora;
    font-size: 36px;
    font-style: normal;
    font-weight: 400;
    line-height: 42px;
    text-transform: capitalize;
    align-self: flex-start;
    &.image-upload-h2 {
      margin: 30px 0;
      text-align: left;
    }
  }
  .image-upload-p {
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.16px;
    text-align: left;
    margin-bottom: 20px;
  }
  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-top: 20px;
    p {
      font-family: Inter;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.16px;
      text-align: center;
    }
    button {
      width: 100%;
    }
    &.login-wrapper {
      margin-bottom: 100px;
    }
    &.form-container {
      max-width: 650px;
      width: 100%;
      box-sizing: border-box;
    }
  }
  input[type="file"] {
    height: unset;
    max-height: unset;
  }
  .file-upload-wrapper {
    display: flex;
    flex-flow: column wrap;
    align-items: flex-start;
    justify-content: space-between;
    margin-top: 30px;
    margin-bottom: 100px;
    box-sizing: border-box;
    width: 100%;
    max-width: 650px;
    .upper-section {
      display: flex;
      flex-flow: row wrap;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      input {
        flex: 1;
        color: #252525;
        &:hover {
          cursor: pointer;
        }
      }
    }
    .lower-section {
      display: flex;
      flex-flow: column wrap;
      align-items: center;
      justify-content: flex-start;
      gap: 10px;
      margin-top: 20px;
      .checkbox-container {
        position: relative;
        .md-checkbox {
          position: absolute;
          opacity: 0; // Hide the default checkbox

          + label {
            display: inline-block;
            cursor: pointer;
            padding-left: 26px; // Space for checkbox
            line-height: 18px;
            vertical-align: middle;

            &:before,
            &:after {
              content: "";
              position: absolute;
              transition: all 0.3s;
            }

            &:before {
              left: 0;
              top: 0;
              width: 18px;
              height: 18px;
              border: 2px solid #757575; // Border color
              background: transparent;
            }

            &:after {
              font-family: "Material Icons";
              content: "\e876"; // Using Unicode checkmark
              top: 0px; // Adjust position to center the checkmark
              left: 0px;
              font-size: 18px;
              color: white;
              transform: scale(0); // Scale down to hide
              transform-origin: 50% 50%;
            }
          }

          &:checked + label {
            &:before {
              border-color: map.get(
                colors.$theme,
                "success"
              ); // Checked border color
              background-color: map.get(
                colors.$theme,
                "success"
              ); // Background color when checked
            }
            &:after {
              transform: scale(1); // Scale up to show
            }
          }

          &:focus + label:before {
            outline: none; // Remove focus outline
          }
        }
      }

      label {
        font-family: Inter;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: 0.16px;
        text-align: left;
      }
    }
    button {
      margin: 0;
      flex: 0; /* Added this line to prevent the button from growing */
      min-width: 250px;
      width: 100%;
      &.submit-button {
        margin-top: 20px;
        min-width: unset;
        padding: 13px 40px;
        align-self: flex-start;
        width: unset;
      }
    }
  }
  .alert {
    border-radius: 4px;
    padding: 16px;
    display: inline-flex;
    flex-flow: column nowrap;
    align-items: flex-start;
    justify-content: center;
    gap: 12px;
    border-radius: 4px;
    border: 2px solid;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.12);
    margin: 20px 0 0;
    width: 100%;
    position: relative;
    &.success {
      border-color: #5bac85;
      background: #f3fff9;
    }
    &.error {
      border-color: #d8000c;
      background: #ffefef;
    }
    &.info {
      border-color: #3783b9;
      background: #f8fcff;
    }
    .alert-icon {
      position: absolute;
      top: 16px;
      right: 16px;
    }
    .alert-content {
      display: flex;
      flex-flow: column nowrap;
      align-items: flex-start;
      justify-content: center;
      gap: 8px;
    }
    .alert-action-wrapper {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
    }
    button.close-button {
      display: flex;
      height: 32px;
      padding: 4px 12px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 10px;
      flex: 1 0 0;
      color: #fff;
      border: none;
      width: 100%;
      font-family: Inter;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 20px; /* 142.857% */
      letter-spacing: 0.14px;
      &.info {
        background: #3783b9;
      }
      &.success {
        background: #5bac85;
      }
      &.error {
        background: #d8000c;
      }
      &:hover {
        cursor: pointer;
      }
    }
  }
  span {
    &.material-symbols-outlined {
      &::after {
        content: "";
        display: block;
        width: 1.2em;
        height: 1.2em;
        position: absolute;
        left: calc(50% - 0.75em);
        top: calc(50% - 0.75em);
        border: 0.15em solid transparent;
        border-right-color: white;
        border-radius: 50%;
        animation: rotate 0.7s linear infinite;
        opacity: 0;
        animation: rotate 2s infinite linear;
      }
    }
  }
}
</style>
